import { ElementType } from '@ritabygglov/core';
import { useCallback, useState } from 'react';
import { UpdateCommand } from '../../../commands/UpdateCommand';
import { worldPx } from '../../../helpers/handleScale';
import { MIN_RADIUS_HANDLE_OFFSET_PX } from '../../../helpers/handleTokens';
import {
  getCornerBisector,
  makeRadiusCursorUrl,
} from '../../../helpers/utility';
import { CommandManager } from '../../../commands/CommandManager';
import CanvasSession from '../../../services/CanvasSession';
import {
  AppState,
  ClientCoordinates,
  SelectionMode,
  SelectionModes,
  ViewBox,
} from '../../../types';
import { extractRadiusPointIndex } from '../utils/typeGuards';

interface RadiusDragInfo {
  elementId: string;
  pointIndex: number;
}

export interface RadiusDraggingDeps {
  appState: AppState;
  session: CanvasSession;
  commandManager: CommandManager;
  selectionMode: SelectionMode;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  viewBox: ViewBox;
}

export const useRadiusDragging = (hooks: RadiusDraggingDeps) => {
  const {
    appState,
    session,
    commandManager,
    setSelectionMode,
    selectionMode,
    setSelectedElements,
    viewBox,
  } = hooks;

  const [radiusDragInfo, setRadiusDragInfo] = useState<RadiusDragInfo | null>(
    null
  );

  const onStartRadiusDrag = useCallback(
    (_clientCoordinates: ClientCoordinates, id: string) => {
      const elementId = id.split('-radius')[0];
      const pointIndex = extractRadiusPointIndex(id);
      const element = appState.elements[elementId];
      if (!element || element.type !== ElementType.Polyline) return;

      document.body.style.cursor = makeRadiusCursorUrl();

      setRadiusDragInfo({ elementId, pointIndex });
      setSelectedElements([elementId]);
      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.RadiusDragging,
      });
    },
    [appState.elements, setSelectionMode, selectionMode, setSelectedElements]
  );

  const onRadiusDragging = useCallback(
    (clientCoordinates: ClientCoordinates, isEphemeral = true) => {
      if (!radiusDragInfo) return;

      const element = appState.elements[radiusDragInfo.elementId];
      if (!element || element.type !== ElementType.Polyline) return;

      const { points } = element;
      const { pointIndex } = radiusDragInfo;

      const isClosed = points.some((p) => p.connectingPointIndex !== undefined);
      const isValidCorner =
        (isClosed && pointIndex === 0) ||
        (pointIndex > 0 && pointIndex < points.length - 1);
      if (!isValidCorner) return;

      const point = points[pointIndex];
      const prevIndex =
        isClosed && pointIndex === 0 ? points.length - 2 : pointIndex - 1;
      const prev = points[prevIndex];
      const next = points[pointIndex + 1];

      const bisector = getCornerBisector(prev, point, next);
      if (!bisector) return;
      const { bisDirX, bisDirY, maxRadius } = bisector;

      const vmx = clientCoordinates.x - point.x;
      const vmy = clientCoordinates.y - point.y;
      const projectedDist = vmx * bisDirX + vmy * bisDirY;

      const minOffset = worldPx(MIN_RADIUS_HANDLE_OFFSET_PX, viewBox.scale);
      const newRadius =
        projectedDist <= minOffset ? 0 : Math.min(projectedDist, maxRadius);

      const updatedPoints = points.map((p, i) =>
        i === pointIndex ? { ...p, cornerRadius: newRadius } : p
      );
      const updatedElement = { ...element, points: updatedPoints };

      const command = new UpdateCommand(
        [updatedElement],
        [element],
        session.currentUserId,
        isEphemeral
      );
      commandManager.executeCommand(command);
    },
    [
      radiusDragInfo,
      appState.elements,
      session.currentUserId,
      commandManager,
      viewBox.scale,
    ]
  );

  const onStopRadiusDrag = useCallback(() => {
    if (radiusDragInfo) {
      document.body.style.cursor = '';
      setRadiusDragInfo(null);
      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.None,
      });
    }
  }, [radiusDragInfo, setSelectionMode, selectionMode]);

  return { onStartRadiusDrag, onRadiusDragging, onStopRadiusDrag };
};
